import * as React from "react";
import { useContext } from "react";
import { Route, RouteProps, Redirect, useLocation } from "react-router";
import { RouteType } from "@socialcoach/services";
import { SessionContext } from "@/providers";
import { ALL_USER_ROLES, SocialCoachSession } from "@/services/bedrock";
import { userDefaultRedirectUrl } from "@/utils";
import { Spinner } from "@/components/ui/spinner";

interface Props extends RouteProps {
  routeType?: RouteType;
  loaderComponent?: React.ReactNode;
  reservedGroups?: string[];
}

export const SCSessionRoute: React.FC<Props> = ({
  routeType = RouteType.PRIVATE_ROUTE,
  reservedGroups = ALL_USER_ROLES,
  loaderComponent,
  component: Component,
  render,
  ...rest
}) => {
  const { session, loading } = useContext(SessionContext);
  const location = useLocation();

  if (import.meta.env.DEV && import.meta.env.VITE_SHOW_ROUTE_CHANGES === "true") {
    console.log("SCSessionRoute Evaluated:", {
      loading,
      hasSession: !!session,
      path: rest.path,
      role: session?.userRole,
    });
  }

  let effectiveRoleForRouting = session?.userRole || "";
  if (effectiveRoleForRouting === "ASSISTANT_COACH") {
    effectiveRoleForRouting = "COACH";
  }

  const shouldRenderExpectedRoute =
    (routeType === RouteType.PRIVATE_ROUTE && !!session && reservedGroups.includes(effectiveRoleForRouting)) ||
    (routeType === RouteType.PUBLIC_ROUTE && !session);

  const getRedirectTarget = () => {
    const from = (location.state as any)?.from;

    if (from && routeType === RouteType.PUBLIC_ROUTE && from.pathname !== location.pathname) {
      return from;
    }

    const url = userDefaultRedirectUrl(session);
    const [pathname, search] = url.split("?");

    // Safety check: Prevent infinite redirect loops if the redirect path is the same as the current path
    if (pathname === location.pathname) {
      return {
        pathname: "/notfound",
        search: undefined,
        state: undefined,
      };
    }

    return {
      pathname,
      search,
      state: { from: location },
    };
  };

  if (loading) {
    return (
      <Route
        {...rest}
        render={() =>
          loaderComponent || (
            <div className="flex h-screen w-screen items-center justify-center bg-slate-50/50">
              <Spinner className="h-10 w-10 text-primary" />
            </div>
          )
        }
      />
    );
  }

  return (
    <Route
      {...rest}
      render={props => {
        if (shouldRenderExpectedRoute) {
          if (Component) {
            return <Component {...props} />;
          }
          if (render) {
            return render(props);
          }
          return null;
        }

        return <Redirect exact to={getRedirectTarget()} />;
      }}
    />
  );
};
